import { Box, Container } from '@mui/material';
import { useLocalStorage } from '@uidotdev/usehooks';
import * as date from 'date-fns';
import { useState } from 'react';
import { RouteObject } from 'react-router-dom';
import useSWR from 'swr';
import { PageSpinner, Video } from '~/components';
import { ChartCard } from '~/components/ChartCard';
import { PageHeader } from '~/components/PageHeader';
import { useFarm } from '~/hooks/useFarm';
import { applyStandardDeviation1SD,applyStandardDeviationpoint5SD  } from '~/utils/stddev';
import {
  birdcountavgCsvRaw,
  birdcountstddevCsvRaw,
  isfeedingavgCsvRaw,
  isfeedingstddevCsvRaw,
  weightavgCsvRaw,
  weightstddevCsvRaw,
  percentavgCsvRaw,
  percentstddevCsvRaw,
} from './FeederPastData/csvIndex';

export const feederRoute: RouteObject = {
  path: '/feeder',
  element: <Feeder />,
}

const defaultFrom = date.subWeeks(new Date(), 6)
const defaultTo = date.addDays(new Date(), 1)

function Feeder() {
  const { farm, isLoading: isLoadingFarm, accessStartDate } = useFarm()

  const [from, setFrom] = useLocalStorage('settings.from', date.formatISO(defaultFrom, { representation: 'date' }))
  const [to, setTo] = useLocalStorage('settings.to', date.formatISO(defaultTo, { representation: 'date' }))
  const [params, setParams] = useState({ from, to })
  
  // Convert access start date to Date object for date picker minDate
  const minDate = accessStartDate ? date.parseISO(accessStartDate) : null

  const { data: feederData = [], isLoading: isLoadingFeeder } = useSWR(
    farm?.id ? `/farms/${farm.id}/insights/feeder?dateFrom=${params.from}&dateTo=${params.to}` : null)

  const { data: boutData = [], isLoading: isLoadingBouts } = useSWR(
    farm?.id ? `/farms/${farm.id}/insights/feeding-bout?dateFrom=${params.from}&dateTo=${params.to}` : null)

  const { data: media } = useSWR(
    farm?.id ? `/farms/${farm.id}/media/feeder/${params.to.replace(/-/g, '')}` : null)

  const parseNumberLines = (text: string): number[] =>
  text
    .trim()
    .split(/\r?\n/)
    .map(line => Number(line.trim()))
    .filter(n => !Number.isNaN(n));



function computeCycleLength(birdAges: number[]): number {
  let maxBeforeReset = 0;

  for (let i = 0; i < birdAges.length; i++) {
    const age = birdAges[i];

    // When we hit a new cycle start (age === 1), we stop reading previous cycle
    if (age === 1 && i !== 0) break;

    if (age > maxBeforeReset) maxBeforeReset = age;
  }

  return maxBeforeReset;
}


 


// Past data from CSV objects
const birdcountavgList = parseNumberLines(birdcountavgCsvRaw);
const birdcountstddevList = parseNumberLines(birdcountstddevCsvRaw);
const isfeedingstddev = parseNumberLines(isfeedingstddevCsvRaw);
const isfeedingAvg = parseNumberLines(isfeedingavgCsvRaw);
const percentstddev = parseNumberLines(percentstddevCsvRaw);
const percentavg = parseNumberLines(percentavgCsvRaw);
const weightstddev = parseNumberLines(weightstddevCsvRaw);
const weightavg = parseNumberLines(weightavgCsvRaw);

// // cycle based on age values in the table 
const ages = feederData.map((d: any) => Number(d.birdage ?? 0));
const CYCLE = computeCycleLength(ages);

// safety fallback 
const cycleSafe = CYCLE > 0 ? CYCLE : Math.max(
  isfeedingAvg.length,
  birdcountavgList.length,
  percentavg.length,
  weightavg.length,
  1
);

//conditional to check if the farm ID is experiment moypark
const isMoyParkFarm = farm?.id === "experiment-moypark";

const AGE_INVALID = Number.MAX_SAFE_INTEGER;
const birdCount = feederData.map((d: any) => {

  const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;
  const idx = wrappedAge - 1;
  //  if wrappedAge exceeds CSV range, do not render any past data
  const outOfRange = idx < 0 || idx >= isfeedingAvg.length;

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.birdCountAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }

  const pastavg = Math.max(0,(birdcountavgList[idx] ?? 0));
  const realstdDev = birdcountstddevList[idx] ?? 0;;

    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg,
      avg:d.birdCountAvg,
      age:wrappedAge,
      stddev: applyStandardDeviation1SD(pastavg, realstdDev),
      stddevp5:applyStandardDeviationpoint5SD(pastavg, realstdDev),

    }
  })

const isFeeding = feederData.map((d: any, i: number) => {
     const birdage = Number(d.birdage ?? d.birdAge ?? d.age);
     const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;


      const idx = wrappedAge - 1;  

      //  if wrappedAge exceeds CSV range ,dp not render any past data
  const outOfRange = idx < 0 || idx >= birdcountavgList.length;
   const pastavg = Math.max(0,(isfeedingAvg[idx] ?? 0));
      const realstdDev = isfeedingstddev[idx] ?? 0;

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.isFeedingAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }


    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg,
      avg:d.isFeedingAvg,
      age:wrappedAge,
      stddev: applyStandardDeviation1SD(pastavg, realstdDev ),
      stddevp5:applyStandardDeviationpoint5SD(pastavg,realstdDev),
     
    }
  })

const percentageFeeding = feederData.map((d: any , i: number) => {
    const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;
      const idx = wrappedAge - 1;
      
      //  if wrappedAge exceeds CSV range,do not render any past data
  const outOfRange = idx < 0 || idx >= percentavg.length;
   const pastavg = Math.max(0,(percentavg[idx] ?? 0));
      const realstdDev = percentstddev[idx] ?? 0;;


 
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.percentageFeedingAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }
      
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg,
      avg: d.percentageFeedingAvg,
       age:wrappedAge,
      stddev: applyStandardDeviation1SD(pastavg, realstdDev),
      stddevp5:applyStandardDeviationpoint5SD(pastavg, realstdDev),
     
    }
  })

  const birdWeight = boutData.map((d: any,  i: number) => {
      const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;

      const idx = wrappedAge - 1;
       
            //  if wrappedAge exceeds CSV range, do not render any past data
  const outOfRange = idx < 0 || idx >= weightavg.length;

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.weightAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }
  
      const pastavg = Math.max(0,(weightavg[idx] ?? 0));
      const realstdDev = weightstddev[idx] ?? 0;


    return {
     datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg,
      avg:d.weightAvg,
      age:wrappedAge,
      stddev: applyStandardDeviation1SD(pastavg, realstdDev),
      stddevp5:applyStandardDeviationpoint5SD(pastavg, realstdDev),
     
    }
  })

  const handleApply = () => {
    setParams({ from, to })
  }

  return (
    <Container maxWidth={false} sx={{ pb: '24px' }}>
      <PageHeader title="Feeder" from={from} to={to} setFrom={setFrom} setTo={setTo} handleApply={handleApply}minDate={minDate} />
      {(isLoadingFarm || isLoadingFeeder|| isLoadingBouts) && <PageSpinner />}
      {!isLoadingFarm && !isLoadingFeeder && !isLoadingBouts && (
      
        <Box display={"grid"} gridTemplateColumns={"repeat(auto-fill, minmax(500px, 700px))"} gap={2}>
          {media?.url &&< Video height='375px' width='100%' src={media.url} />}
          <ChartCard title="Bird Count" data={birdCount} />
          <ChartCard title="Is Feeding" data={isFeeding} />
          <ChartCard title="% Feeding" data={percentageFeeding} />
          <ChartCard title="Bird Weight" data={birdWeight} />
        </Box>
      )}
    </Container>
  )
}